import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../state/AppContext";
import PetalLogo from "../components/PetalLogo";
import { LogIn, LogOut, BookOpen, Star, Image as ImageIcon, Users } from "lucide-react";

const TOKEN_KEY = "scl_parent_token";

export default function ParentScreen() {
  const [me, setMe] = useState(null);      // { email, name, picture, students }
  const [homework, setHomework] = useState(null); // { assignments, message }
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Handle Emergent Auth callback (fragment #session_id=)
  useEffect(() => {
    const hash = window.location.hash || "";
    if (hash.includes("session_id=")) {
      const sid = new URLSearchParams(hash.slice(1)).get("session_id");
      if (sid) {
        axios.post(`${API}/auth/parent/callback`, { session_id: sid })
          .then((r) => {
            if (r.data?.session_token) {
              localStorage.setItem(TOKEN_KEY, r.data.session_token);
              window.history.replaceState(null, "", "/parent");
              loadAll(r.data.session_token);
            }
          })
          .catch((e) => setErr(e?.response?.data?.detail || "Sign-in failed"))
          .finally(() => setLoading(false));
        return;
      }
    }
    const saved = localStorage.getItem(TOKEN_KEY);
    if (saved) loadAll(saved); else setLoading(false);
  }, []);

  const loadAll = async (token) => {
    setLoading(true);
    try {
      const [meRes, hwRes] = await Promise.all([
        axios.get(`${API}/parent/me`, { params: { token } }),
        axios.get(`${API}/parent/homework`, { params: { token } }),
      ]);
      setMe(meRes.data); setHomework(hwRes.data);
    } catch (e) {
      if (e?.response?.status === 401) { localStorage.removeItem(TOKEN_KEY); setMe(null); }
      else setErr(e?.response?.data?.detail || "Could not load");
    } finally { setLoading(false); }
  };

  const signIn = () => {
    const redirectUrl = window.location.origin + "/parent";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  const signOut = () => { localStorage.removeItem(TOKEN_KEY); setMe(null); setHomework(null); };

  if (loading) return <div className="min-h-screen bg-scl-cream flex items-center justify-center text-scl-charcoal/60">Loading…</div>;

  if (!me) {
    return (
      <div className="min-h-screen bg-scl-cream flex flex-col items-center justify-center px-6" data-testid="parent-signin">
        <div className="w-full max-w-sm bg-white rounded-2xl p-6 shadow-warm text-center">
          <div className="mx-auto mb-3 w-16 h-16 rounded-full bg-scl-teal/10 flex items-center justify-center"><PetalLogo size={48} /></div>
          <h1 className="font-serif text-2xl text-scl-charcoal font-bold mb-1">Family sign-in</h1>
          <p className="text-scl-charcoal/70 text-sm mb-4">See your child&apos;s homework and classroom updates.</p>
          {err && <div className="text-xs text-scl-brick bg-scl-brick/10 rounded-lg px-3 py-2 mb-3">{err}</div>}
          <button onClick={signIn} data-testid="parent-signin-btn" className="w-full h-12 rounded-full bg-scl-coral text-white font-semibold shadow-warm-sm hover:brightness-105 flex items-center justify-center gap-2">
            <LogIn className="w-4 h-4" /> Sign in with Google
          </button>
          <a href="/" className="mt-4 block text-xs text-scl-charcoal/60 hover:text-scl-teal underline">Back to app</a>
        </div>
      </div>
    );
  }

  const students = me.students || [];
  const items = homework?.assignments || [];
  return (
    <div className="min-h-screen bg-scl-cream pb-16" data-testid="parent-dashboard">
      <header className="bg-white shadow-warm-sm">
        <div className="max-w-2xl mx-auto px-5 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {me.picture && <img src={me.picture} alt="" className="w-9 h-9 rounded-full" />}
            <div className="min-w-0">
              <div className="font-serif font-bold text-scl-charcoal leading-tight truncate">{me.name || me.email}</div>
              <div className="text-[11px] text-scl-charcoal/55 truncate">{me.email}</div>
            </div>
          </div>
          <button onClick={signOut} data-testid="parent-signout-btn" className="min-h-[44px] px-3 rounded-full border border-scl-border text-xs font-semibold text-scl-charcoal hover:bg-scl-cream flex items-center gap-1.5">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-5 py-6">
        <div className="bg-white rounded-2xl p-5 shadow-warm-sm mb-4">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-scl-teal" />
            <div className="text-[10px] uppercase tracking-widest font-bold text-scl-teal">Your students</div>
          </div>
          {students.length === 0 ? (
            <p className="text-sm text-scl-charcoal/70 leading-relaxed">{homework?.message || "No students linked yet. Ask your teacher to invite you — access to homework is invite-only."}</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {students.map((s) => (
                <span key={s.id} className="px-3 py-1 rounded-full bg-scl-mustard/40 text-scl-charcoal text-xs font-semibold" data-testid={`parent-student-${s.id}`}>
                  {s.student_name}{s.grade_class ? ` · ${s.grade_class}` : ""}
                </span>
              ))}
            </div>
          )}
        </div>

        <h2 className="font-serif text-xl font-bold text-scl-charcoal mb-3">Homework & classroom updates</h2>
        {items.length === 0 ? (
          <div className="bg-white rounded-2xl p-6 shadow-warm-sm text-center text-scl-charcoal/60 text-sm" data-testid="parent-empty">
            {students.length === 0 ? "You&apos;ll see your child&apos;s work here once a teacher links you." : "Nothing to show yet."}
          </div>
        ) : (
          <div className="grid gap-3" data-testid="parent-homework-list">
            {items.map((a) => (
              <div key={a.id} className="bg-white rounded-2xl overflow-hidden shadow-warm-sm" data-testid={`parent-homework-${a.id}`}>
                {a.file_link && a.file_link.match(/\.(jpg|jpeg|png|webp|gif)(\?|$)/i) && (
                  <img src={a.file_link} alt="" className="w-full max-h-64 object-cover" />
                )}
                <div className="p-4">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-scl-charcoal/60 font-semibold mb-1">
                    {a.upload_type === "student_work_example" ? <Star className="w-3.5 h-3.5" /> : a.upload_type === "classroom_photo" ? <ImageIcon className="w-3.5 h-3.5" /> : <BookOpen className="w-3.5 h-3.5" />}
                    <span>{(a.upload_type || "").replaceAll("_", " ")}</span>
                    <span className="ml-auto normal-case tracking-normal text-scl-charcoal/45 font-normal">{a.created_at ? new Date(a.created_at).toLocaleDateString() : ""}</span>
                  </div>
                  <div className="font-serif text-base font-bold text-scl-charcoal">{a.subject_name || a.grade_class || "SCL"}</div>
                  {a.notes && <p className="text-sm text-scl-charcoal/75 mt-1 leading-relaxed">{a.notes}</p>}
                  {a.file_link && !a.file_link.match(/\.(jpg|jpeg|png|webp|gif)(\?|$)/i) && (
                    <a href={a.file_link} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs text-scl-teal hover:underline">Open attachment</a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
